import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useNotification, errorNotificationBuilder, NotificationTypes } from '@css-xp/base';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { NOTIFICATION_DISMISS_TIME } from '../constants/invoices';
import { fetchOrdersByOrderLineId } from '../queries/fetch-orders-by-order-line-id';

interface Props {
  txaId: string;
  orderLineIds: string[];
  shouldSkipFetch?: boolean;
}
export const useOrderNumberData = ({ txaId, orderLineIds, shouldSkipFetch }: Props) => {
  const { sendErrorNotification, setNotificationSent } = useNotification();
  const orFilterValue = orderLineIds?.map((id) => {
    return {
      key: 'orderLineItemsId',
      value: id,
    };
  });

  const { data, loading, error } = useQuery<
    generatedTypesNew.FetchOrdersByOrderLineIdQuery,
    generatedTypesNew.FetchOrdersByOrderLineIdQueryVariables
  >(fetchOrdersByOrderLineId, {
    variables: {
      transactionAccountId: txaId,
      filterOptions: {
        or: orFilterValue,
      },
    },
    skip: shouldSkipFetch || !orFilterValue?.length || !txaId,
  });

  useEffect(() => {
    // Show error notification if fetch fails
    if (error) {
      const errorNotificationOptions = errorNotificationBuilder()
        .withId('order-number-fetch-error')
        .withDismissAfter(NOTIFICATION_DISMISS_TIME)
        .isVersion(NotificationTypes.FLAG);

      const options = errorNotificationOptions
        .withType('warning')
        .withHeader('Associated order numbers are not available')
        .withMessage('An error occurred while fetching associated order numbers. Please try again after some time.')
        .build();

      sendErrorNotification(options);
      setNotificationSent(true);
    }
    // Note: Only error is added as a dependency to avoid infinite loop
  }, [error]);

  return { data, loading };
};
